import { useCallback, useContext, useEffect, useRef } from 'react';
import { Settings } from '@/models/settings';
import { SettingsContext, ThemeContext } from '@/state/contexts';
import { usePageLoading } from '@/hooks/usePageLoading';

export function useSettings() {
  const { settings, setSettings, isLoading } = useContext(SettingsContext);
  const { darkTheme, setDarkTheme } = useContext(ThemeContext);

  usePageLoading(!settings); // only show page loader when first pulling settings

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateSetting = useCallback(
    (partial: Partial<Settings>, debounceMs = 0) => {
      // Preview theme immediately
      if (typeof partial.darkMode === 'boolean') {
        setDarkTheme(partial.darkMode);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (debounceMs > 0) {
        debounceRef.current = setTimeout(
          () => setSettings(partial),
          debounceMs
        );
      } else {
        setSettings(partial);
      }
    },
    [setSettings, setDarkTheme]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  return { settings, isLoading, darkTheme, updateSetting };
}
